/**
 * Zone-Based Book Operations Hook
 * 
 * This hook provides book-related operations using the zone-based architecture.
 * It maintains backward compatibility while leveraging the improved zone system.
 * 
 * Migration from: useBookOperations.js
 * Part of: Full Zone Migration
 */

import { useCallback, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSharedData } from '../core/useZoneHooks';
import {
  fetchBooks,
  fetchBookProfile,
  fetchAllBooks,
  deleteBook as deleteBookThunk,
  setBookSelection,
  createBook as createBookThunk,
  updateBook as updateBookThunk
} from '../../slices/bookWorksheetDataSlice';

export const useBookOperationsZoneBased = () => {
  const dispatch = useDispatch();
  const sharedData = useSharedData();

  // Extract relevant data from zone-based state with defensive defaults
  const {
    books = [],
    selections = {},
    loading = {},
    errors = {},
    lastUpdated = {},
    bookProfiles = {},
    actions = {}
  } = sharedData || {};

  const { litGroupId, bookId: selectedBookId } = selections;

  // Find selected book from books array
  const selectedBook = useMemo(() =>
    books.find(book => book.uuid === selectedBookId) || null,
    [books, selectedBookId]
  );

  // Get selected book profile if available
  const selectedBookProfile = useMemo(() =>
    selectedBookId ? bookProfiles[selectedBookId] || null : null,
    [bookProfiles, selectedBookId]
  );

  // Get books for current litGroup
  const litGroupBooks = useMemo(() =>
    litGroupId ? books.filter(book => book.litGroupId === litGroupId) : [],
    [books, litGroupId]
  );

  // Loading/error states
  const booksLoading = loading.books || false;
  const booksError = errors.books || null;
  const booksLastUpdated = lastUpdated.books || null;

  // Cache duration (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Operations with zone-based architecture
  const loadBooks = useCallback(async (groupId) => {
    const targetGroupId = groupId || litGroupId;
    if (!targetGroupId) {
      console.error('[useBookOperationsZoneBased] No litGroupId provided');
      return Promise.reject(new Error('No literary group ID provided'));
    }

    // Check cache
    const now = Date.now();
    if (booksLastUpdated && (now - booksLastUpdated < CACHE_DURATION)) {
      const cachedBooks = books.filter(book => book.litGroupId === targetGroupId);
      if (cachedBooks.length > 0) {
        console.log('[useBookOperationsZoneBased] Using cached books');
        return Promise.resolve({ type: 'cache/hit', data: cachedBooks });
      }
    }

    console.log('[useBookOperationsZoneBased] Loading books for group:', targetGroupId);
    return dispatch(fetchBooks(targetGroupId)).unwrap();
  }, [dispatch, litGroupId, books, booksLastUpdated, CACHE_DURATION]);

  const loadAllBooks = useCallback(async () => {
    console.log('[useBookOperationsZoneBased] Loading all books');
    return dispatch(fetchAllBooks()).unwrap();
  }, [dispatch]);

  const loadBook = useCallback(async (bookId) => {
    if (!bookId) {
      console.error('[useBookOperationsZoneBased] No bookId provided');
      return Promise.reject(new Error('No book ID provided'));
    }

    // Check if we already have this book's data
    const existingBook = books.find(b => b.uuid === bookId);
    if (existingBook && bookProfiles[bookId]) {
      console.log('[useBookOperationsZoneBased] Book already loaded:', bookId);
      return Promise.resolve({ type: 'cache/hit', data: existingBook });
    }

    console.log('[useBookOperationsZoneBased] Loading book profile:', bookId);
    return dispatch(fetchBookProfile(bookId)).unwrap();
  }, [dispatch, books, bookProfiles]);

  const selectBook = useCallback((bookId) => {
    console.log('[useBookOperationsZoneBased] Selecting book:', bookId);
    if (actions?.setBookSelection) {
      actions.setBookSelection(bookId);
    } else {
      dispatch(setBookSelection(bookId));
    }
  }, [actions, dispatch]);

  const createBook = useCallback(async (data) => {
    if (!litGroupId) {
      throw new Error('No literary group selected for book creation');
    }
    console.log('[useBookOperationsZoneBased] Creating book with data wrapper in litGroup:', litGroupId);
    try {
      const result = await dispatch(createBookThunk({
        litGroupId,
        ...data
      })).unwrap();
      console.log('[useBookOperationsZoneBased] Book created successfully:', result);
      return result;
    } catch (error) {
      console.error('[useBookOperationsZoneBased] Error creating book:', error);
      throw error;
    }
  }, [litGroupId, dispatch]);

  const updateBook = useCallback(async (bookIdOrData, dataParam) => {
    // Handle both object style { bookId, data } and separate arguments (bookId, data)
    let bookId, data;

    if (typeof bookIdOrData === 'object' && bookIdOrData.bookId) {
      // Object style: { bookId, data }
      bookId = bookIdOrData.bookId;
      data = bookIdOrData.data;
    } else {
      // Separate arguments style: (bookId, data)
      bookId = bookIdOrData;
      data = dataParam;
    }

    if (!bookId) {
      throw new Error('No book ID provided for update');
    }

    console.log('[useBookOperationsZoneBased] Updating book with data wrapper:', bookId);
    try {
      const result = await dispatch(updateBookThunk({
        bookUuid: bookId,
        profileData: data
      })).unwrap();
      console.log('[useBookOperationsZoneBased] Book updated successfully:', result);
      return result;
    } catch (error) {
      console.error('[useBookOperationsZoneBased] Error updating book:', error);
      throw error;
    }
  }, [dispatch]);

  const deleteBook = useCallback(async (bookIdOrObj, options = {}) => {
    // Handle both calling conventions:
    // 1. deleteBook('uuid', { options })
    // 2. deleteBook({ bookId: 'uuid', deleteType: 'hard', options: {} })
    let bookId;
    let resolvedOptions = options;

    if (typeof bookIdOrObj === 'object' && bookIdOrObj !== null && bookIdOrObj.bookId) {
      bookId = bookIdOrObj.bookId;
      resolvedOptions = bookIdOrObj.options || options;
    } else {
      bookId = bookIdOrObj;
    }

    if (!bookId) {
      throw new Error('No book ID provided for deletion');
    }
    console.log('[useBookOperationsZoneBased] Deleting book:', bookId);
    return dispatch(deleteBookThunk({ bookId, options: resolvedOptions })).unwrap();
  }, [dispatch]);

  const getBookDeleteImpact = useCallback((bookId) => {
    // Calculate impact of deleting a book
    // Must return a Promise since DeleteBookModal calls .then() on the result
    const book = books.find(b => b.uuid === bookId);
    if (!book) {
      return Promise.resolve({ characters: 0, variations: 0 });
    }

    // This would need actual implementation to count related entities
    // For now, return placeholder wrapped in Promise
    return Promise.resolve({
      bookName: book.name || 'Unknown Book',
      characters: 0, // Would need to count from characterZone
      variations: 0  // Would need to count from variationZone
    });
  }, [books]);

  // Track which litGroups we've already fetched to prevent infinite loops
  // when a litGroup genuinely has no books
  const fetchedLitGroupsRef = useRef(new Set());

  // Auto-fetch books when litGroup changes
  useEffect(() => {
    if (litGroupId && litGroupBooks.length === 0 && !booksLoading && !fetchedLitGroupsRef.current.has(litGroupId)) {
      console.log('[useBookOperationsZoneBased] Auto-loading books for litGroup:', litGroupId);
      fetchedLitGroupsRef.current.add(litGroupId);
      loadBooks(litGroupId);
    }
  }, [litGroupId, litGroupBooks.length, booksLoading, loadBooks]);

  // Return API maintaining backward compatibility
  return {
    // Data
    books: litGroupBooks, // Books for current litGroup
    allBooks: books,      // All books in state
    selectedBook,
    selectedBookProfile,
    selectedBookId,
    litGroupId,

    // States
    booksLoading,
    booksError,
    booksLastUpdated,

    // Operations
    fetchBooks: loadBooks,
    fetchAllBooks: loadAllBooks,
    fetchBook: loadBook,
    selectBook,
    createBook,
    updateBook,
    deleteBook,
    getBookDeleteImpact,

    // Convenience aliases for backward compatibility
    loadBooks,
    loadBook,

    // Zone data access for advanced use
    sharedData
  };
};

// Export as default
export default useBookOperationsZoneBased;

// Also export with old name for easier migration
export const useBookOperations = useBookOperationsZoneBased;
